/**
 * CE QUI DESCEND SUR LE POSTE, ET CE QUI N'Y DESCEND PAS.
 *
 * ── LA RÈGLE ──
 *
 * Aucune donnée à laquelle l'usager n'a pas accès ne doit être téléchargée sur
 * son poste. Ce n'est pas la même exigence que « ne pas l'afficher » : une
 * donnée cachée mais présente en base locale se lit avec les outils du
 * navigateur, survit à la déconnexion, et part avec la machine.
 *
 * ── LE DÉFAUT ──
 *
 * L'hydratation téléchargeait ses collections en bloc, sans regarder les
 * droits : la paie, les contrats, les écritures comptables et la liste des
 * utilisateurs atterrissaient sur le poste d'un vendeur.
 *
 * ── CE QUE CE FILTRE FAIT, ET CE QU'IL NE FAIT PAS ──
 *
 * Il empêche le client honnête de demander ce qu'il n'a pas le droit de lire.
 * Il ne remplace PAS le contrôle serveur : un client modifié appellerait les
 * mêmes URL directement. Neuf routes de lecture n'exigent aujourd'hui aucune
 * permission — c'est là que la règle se tient vraiment, et c'est une décision
 * séparée, mesurée dans le rapport d'impact.
 *
 * ── LES JOKERS ──
 *
 * Le modèle de rôles en connaît deux, et le back-office les applique déjà :
 * `ALL:ALL` (tous les droits) et `ENTITE:ALL` (toutes les actions sur une
 * entité). Les ignorer priverait de tout un administrateur défini par
 * `ALL:ALL` — le cas le plus courant.
 */

/** Une permission accordée, en tenant compte des jokers du modèle. */
export function permissionAccordee(
  exigee: string,
  effectif: ReadonlySet<string>,
): boolean {
  if (effectif.has(exigee)) return true;
  if (effectif.has('ALL:ALL')) return true;
  const entite = exigee.split(':')[0];
  return entite ? effectif.has(`${entite}:ALL`) : false;
}

/**
 * Cette collection peut-elle descendre sur le poste ?
 *
 * Une cible SANS permission déclarée descend toujours : ce sont les données de
 * référence — unités, méthodes de paiement, familles de produits — que toute
 * session doit connaître pour afficher quoi que ce soit, et qui ne disent rien
 * de l'activité de l'entreprise.
 *
 * Une cible AVEC permission déclarée ne descend que si le droit est accordé.
 * L'ensemble vide n'accorde rien : au démarrage, tant que les droits ne sont
 * pas chargés, mieux vaut ne rien télécharger que tout télécharger.
 */
export function hydratationPermise(
  permissionExigee: string | undefined,
  effectif: ReadonlySet<string>,
): boolean {
  if (!permissionExigee) return true;
  return permissionAccordee(permissionExigee, effectif);
}
